import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { ChangePassword } from 'app/models/changePassword';
import { AppService } from 'app/services/app/app.service';
import { RestService } from 'app/services/rest/rest.service';
import { SharedModule } from 'app/shared.module';
import { passwordStrengthValidator } from 'app/validations/password-strength.validator';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule]
})
export class ChangePasswordComponent {
  private readonly destroyRef = inject(DestroyRef);
  readonly form = new FormGroup({
    oldPassword: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    newPassword: new FormControl('', { nonNullable: true, validators: [Validators.required, passwordStrengthValidator()] }),
    passwordConfirm: new FormControl('', { nonNullable: true, validators: [Validators.required] })
  }, {
    validators: group => group.get('newPassword')?.value === group.get('passwordConfirm')?.value
      ? null : { passwordMismatch: true }
  });
  readonly fields = [
    { name: 'oldPassword', label: 'changePassword.current', autocomplete: 'current-password', visible: false },
    { name: 'newPassword', label: 'changePassword.new', autocomplete: 'new-password', visible: false },
    { name: 'passwordConfirm', label: 'passwordConfirm', autocomplete: 'new-password', visible: false }
  ];
  saving = false;
  failed = false;

  constructor(private appService: AppService, private restService: RestService) {}

  get passwordStrength(): number {
    const control = this.form.controls.newPassword;
    return control.value ? control.errors?.['strength'] ?? 5 : 0;
  }

  get strengthLabel(): string {
    return this.passwordStrength <= 2 ? 'changePassword.strength.weak'
      : this.passwordStrength < 5 ? 'changePassword.strength.medium' : 'changePassword.strength.strong';
  }

  fieldErrors(name: string): string[] {
    const control = this.form.get(name);
    if (!control?.touched) return [];
    if (control.hasError('required')) return ['password.errors.required'];
    const errors: string[] = [];
    if (control.hasError('minLength')) errors.push('password.errors.minlength');
    for (const key of ['missingLowercase', 'missingUppercase', 'missingDigit', 'missingSpecial']) {
      if (control.hasError(key)) errors.push(`password.errors.${key}`);
    }
    if (name === 'passwordConfirm' && this.form.hasError('passwordMismatch')) {
      errors.push('password.errors.passwordNotMatch');
    }
    return errors;
  }

  changePassword(): void {
    if (this.saving) return;
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const { oldPassword, newPassword } = this.form.getRawValue();
    const payload: ChangePassword = { userId: this.appService.usuario.id, oldPassword, newPassword };
    this.saving = true;
    this.failed = false;
    this.form.disable({ emitEvent: false });
    this.restService.changePassword(payload).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.saving = false;
        this.form.enable({ emitEvent: false });
      })
    ).subscribe({
      next: () => {
        this.form.reset();
        this.fields.forEach(field => field.visible = false);
        this.appService.showSuccessToast(this.appService.getTraduccion('changePassword.ok'));
      },
      // The auth interceptor displays the translated errors returned by the API.
      error: () => this.failed = true
    });
  }
}
